/**
 * What each field in a content-library entry actually is.
 *
 * The API describes a content type as a flat array of field names —
 * `['title', 'content', 'writer', 'image']` — and nothing else. No types, no
 * labels, no hint about which one holds an article and which holds a job
 * title. So the form rendered every one of them as an identical single-line
 * box labelled with the raw database name: a blog post typed into the same
 * control as its author's name, under the heading "content".
 *
 * Until the API describes its own fields, the name is the only signal there
 * is, and this is where that guess lives — in one place, rather than as
 * scattered `if (field === 'image')` checks that each knew about a different
 * subset.
 */

/** Fields holding prose: a proper writing area with formatting. */
const LONG_TEXT = new Set([
  'content', 'description', 'body', 'bio', 'text', 'message', 'comment',
  'quote', 'answer', 'summary', 'story', 'about', 'details', 'notes',
  'excerpt', 'overview',
]);

/** Fields holding a picture. */
const IMAGE = new Set([
  'image', 'photo', 'logo', 'avatar', 'picture', 'cover', 'banner',
  'recommender_image',
]);

const DATE = new Set(['date', 'start_date', 'end_date', 'published_at', 'deadline']);
const URL_FIELDS = new Set(['url', 'link', 'website', 'file', 'download']);
const EMAIL = new Set(['email', 'contact_email']);
const PHONE = new Set(['phone', 'mobile', 'tel', 'telephone']);
const BOOLEAN = new Set(['published', 'draft', 'featured', 'active']);

/** Fields the owner never sets by hand. */
export const HIDDEN_FIELDS = new Set([
  '_id', '__v', 'brand_id', 'stage', 'stage_name',
  'creator', 'user_updated', 'created_at', 'updated_at', 'draft',
]);

/**
 * Better words than the database has.
 *
 * Most names read fine once the underscores are gone. These do not: a couple
 * carry a prefix that is noise inside a form already titled "Recommendation",
 * one is misspelled in the schema and cannot be renamed without a migration,
 * and a few are simply not what a person would call the thing.
 */
const LABELS = {
  published_at: 'Publish date',
  writer: 'Author',
  headline: 'Headline',
  partner_name: 'Partner name',
  project_name: 'Project name',
  employment_type: 'Employment type',
  recommender_name: 'Name',
  recommender_image: 'Photo',
  recommender_title: 'Job title',
  recommender_designation: 'Company',
  recommender_description: 'What they said',
  // Misspelled in the schema; correcting it there would orphan every stored
  // value, so it is corrected here where it is read.
  recommneder_relation: 'Relation to you',
  value: 'Figure',
  period: 'Billing period',
  url: 'Link',
  image: 'Cover image',
  published: 'Published',
};

const PLACEHOLDERS = {
  title: 'A short, specific title',
  writer: 'Who wrote it',
  price: '19',
  period: 'a month',
  url: 'https://…',
  value: '250+',
  location: 'City, or an address',
  employment_type: 'Full time, part time, contract…',
};

const HELP = {
  price: 'Just the number and currency as you want it shown.',
  url: 'A full web address, including https://',
  published_at: 'The date shown on the post. Leave it empty to show none.',
  published: 'Off keeps it off the public site, whatever date is set.',
};

const titleCase = (key) => String(key)
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, (c) => c.toUpperCase());

/** What kind of control a field wants. */
export const kindOf = (field) => {
  const f = String(field || '').toLowerCase();
  if (IMAGE.has(f)) return 'image';
  if (LONG_TEXT.has(f)) return 'longtext';
  if (BOOLEAN.has(f)) return 'boolean';
  if (DATE.has(f)) return 'date';
  if (URL_FIELDS.has(f)) return 'url';
  if (EMAIL.has(f)) return 'email';
  if (PHONE.has(f)) return 'tel';
  return 'text';
};

/** Everything the form needs to draw one field. */
export const describeField = (field) => {
  const f = String(field || '');
  const kind = kindOf(f);
  return {
    name: f,
    kind,
    label: LABELS[f.toLowerCase()] || titleCase(f),
    placeholder: PLACEHOLDERS[f.toLowerCase()] || '',
    help: HELP[f.toLowerCase()] || '',
    // Prose and pictures need the room; a job title does not, so short fields
    // pair up two to a row and the form stops being a single tall column.
    fullWidth: kind === 'longtext' || kind === 'image',
  };
};

export default describeField;
